/**
 * Remembers which preview book a /create-comic input set already produced, so a remount (Back then Next, a refresh,
 * "Try again" after a client timeout) re-polls that book instead of paying for a new LLM call and cover.
 * Pure helpers: the storage is passed in and every access is wrapped in try/catch.
 */

export interface PreviewInputs {
  characters: unknown;
  storyTitle: string;
  storyIdea: string;
  settings: unknown;
  photoDataUrl: string;
  /** Story language; English (or none) keeps the key it had before the picker existed. */
  language?: string;
  /** The catalogue story the preview is stamped with (stories-as-comics); none keeps the old key. */
  storyId?: string;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const KEY_PREFIX = 'joonoo:comic-preview:';

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return `{${Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(o[k])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/** Cheap photo fingerprint: first and last 64 chars plus the length of the data URL. */
export function photoFingerprint(dataUrl: string): string {
  return `${dataUrl.slice(0, 64)}|${dataUrl.slice(-64)}|${dataUrl.length}`;
}

function fnv1a(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Stable key for a set of inputs: the same inputs always give the same key, any change gives a new one. */
export function previewInputKey(i: PreviewInputs): string {
  const text = stableStringify({
    c: i.characters,
    t: i.storyTitle.trim(),
    i: i.storyIdea.trim(),
    s: i.settings,
    p: photoFingerprint(i.photoDataUrl),
    l: i.language && i.language !== 'en' ? i.language : undefined,
    st: i.storyId || undefined,
  });
  return `${KEY_PREFIX}${fnv1a(text)}${fnv1a(text.split('').reverse().join(''))}`;
}

export function readStoredPreviewBook(storage: StorageLike | null | undefined, key: string): string | null {
  try {
    const v = storage?.getItem(key);
    return v && /^[\w-]{8,64}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function storePreviewBook(storage: StorageLike | null | undefined, key: string, bookId: string): void {
  try {
    storage?.setItem(key, bookId);
  } catch {
    // Private mode or blocked storage: the page state still covers a Back/Next within the session.
  }
}

export function clearStoredPreviewBook(storage: StorageLike | null | undefined, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    // ignore
  }
}

/** The browser's sessionStorage, or null where it is blocked. */
export function safeSessionStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export type PreviewStart = { kind: 'poll'; bookId: string } | { kind: 'create' };

/**
 * Re-poll the book already made for these inputs; create a new one only when there is none.
 * A book whose preview FAILED (or no longer exists) must have been cleared by the caller, so it is never re-polled.
 */
export function decidePreviewStart(pageBookId: string | null | undefined, storedBookId: string | null | undefined): PreviewStart {
  const bookId = pageBookId || storedBookId;
  return bookId ? { kind: 'poll', bookId } : { kind: 'create' };
}
